import { AllGeoJSON } from "@turf/helpers";
import turf from "../../../shared/utils/turf";
import { CreateHlaWay } from "../../../hla/models/HlaWay";
import { RootStore } from "../../../shared/stores/RootStore";
import { Feature, LineString, MultiPolygon, Point, Polygon } from "geojson";

function centerMapAround(geoJson: AllGeoJSON, map: mapboxgl.Map) {
  const featureCenter = turf.centroid(geoJson);
  map.jumpTo({
    center: featureCenter.geometry.coordinates as mapboxgl.LngLatLike,
    zoom: 16,
  });
}

// just for testing
function addRequestToMap(way: CreateHlaWay, rootStore: RootStore) {
  const { hlaStore } = rootStore;
  const wayId = Math.random().toString();

  hlaStore.setWays(
    hlaStore.ways.concat([
      {
        ...way,
        id: wayId,
        frontFacingLane: way.frontFacingLane
          ? {
              ...way.frontFacingLane,
              id: Math.random().toString(10),
              hlaWayId: wayId,
              score: null,
            }
          : null,
        backFacingLane: way.backFacingLane
          ? {
              ...way.backFacingLane,
              id: Math.random().toString(10),
              hlaWayId: wayId,
              score: null,
            }
          : null,
        createdAt: "",
      },
    ]),
  );
}

function polygonToBbox(
  bufferPolygon: Feature<Polygon | MultiPolygon>,
  map: mapboxgl.Map,
): [mapboxgl.Point, mapboxgl.Point] {
  const bbox = turf.bbox(bufferPolygon);
  const [minLng, minLat, maxLng, maxLat] = bbox;
  const sw = map.project([minLng, minLat]);
  const ne = map.project([maxLng, maxLat]);
  return [sw, ne];
}

function determineSideAndDistance(target: Point, road: LineString) {
  const point = turf.point(target.coordinates);

  // Find the nearest point on the line
  const nearest = turf.nearestPointOnLine(road, point, {
    units: "meters",
  });
  var segmentIndex = nearest.properties.index; // Index of the segment

  // Get the segment points
  const coords = road.coordinates;
  const A = coords[segmentIndex];
  const B = coords[segmentIndex + 1];

  if (B == null) return;

  // Compute vectors
  const ABx = B[0] - A[0]; // Longitude difference
  const ABy = B[1] - A[1]; // Latitude difference
  const APx = target.coordinates[0] - A[0];
  const APy = target.coordinates[1] - A[1];

  // Cross product
  const cross = ABx * APy - ABy * APx;

  return {
    side: cross > 0 ? "left" : "right",
    distance: nearest.properties.dist,
  } as { side: "left" | "right"; distance: number };
}

function flattenLineAndMultiLineFeatures(
  features: Feature[],
): Feature<LineString>[] {
  return features.flatMap((feat) => {
    if (feat.geometry.type === "LineString") {
      const lineString = feat as Feature<LineString>;
      return [lineString];
    } else if (feat.geometry.type === "MultiLineString") {
      return feat.geometry.coordinates.map((coordinates) => {
        return turf.lineString(coordinates, feat.properties, {
          bbox: feat.bbox,
          id: feat.id,
        });
      });
    }
    return [];
  });
}

/*
Split a line using a polygon's boundary and exclude the parts that fall outside of the polygon  
*/
function intersectLineAndPolygon(
  line: Feature<LineString>,
  polygon: Feature<Polygon>,
) {
  const splitResult = turf.lineSplit(line, polygon);

  // if the line is fully inside the polygon, the lineSplit will not return any features
  if (splitResult.features.length === 0) {
    const coords = line.geometry.coordinates;
    const mid = turf.midpoint(coords[0], coords[coords.length - 1]);
    if (turf.booleanPointInPolygon(mid, polygon)) {
      return [line];
    }
  }

  const insideSegments = splitResult.features.filter((segment) => {
    const coords = segment.geometry.coordinates;
    const mid = turf.midpoint(coords[0], coords[coords.length - 1]);
    return turf.booleanPointInPolygon(mid, polygon);
  });

  return insideSegments;
}

function anglesEqualDirectionless(
  deg1: number,
  deg2: number,
  toleranceDeg: number,
) {
  const ignoreDirection = (a: number) => (a >= 180 ? a - 180 : a);
  const diff = Math.abs(ignoreDirection(deg1) - ignoreDirection(deg2));
  return diff <= toleranceDeg || 180 - diff <= toleranceDeg;
}

export const importHlaUtils = {
  centerMapAround,
  addRequestToMap,
  polygonToBbox,
  determineSideAndDistance,
  flattenLineAndMultiLineFeatures,
  intersectLineAndPolygon,
  anglesEqualDirectionless,
};
